/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext, IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    TelemetryConstant,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';

export interface IPopupProps
    extends IComponentProps<{
        product: SimpleProduct | undefined;
        price: ProductPrice | undefined;
        bonusProducts: SimpleProduct[] | undefined;
    }> {
    dialogStrings?: {
        goToCartText: string;
        continueShoppingText: string;
        headerItemOneText: string;
        headerItemFormatText: string;
        headerMessageText: string;
        freePriceText: string;
        originalPriceText: string;
        currentPriceText: string;
    };

    className: string;
    imageSettings?: IImageSettings;
    gridSettings: IGridSettings | undefined;
    productQuantity: number;
    priceComponent: React.ReactNode;
    navigationUrl?: string;
    modalOpen: boolean | undefined;
    telemetryContent?: ITelemetryContent;
    setModalOpen(newValue: boolean): void;
}

const getDimensionsText = (product: SimpleProduct) => {
    return product.Dimensions
        ? product.Dimensions.reduce<string>((str, productDimension) => {
              if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                  if (str) {
                      return `${str}, ${productDimension.DimensionValue.Value}`;
                  }

                  return `${productDimension.DimensionValue.Value}`;
              }

              return str;
          }, '')
        : '';
};

export const renderBonusItemLines = (
    bonusProducts: (SimpleProduct & { shipped?: boolean })[],
    context: ICoreContext,
    className: string,
    shippedLabel?: string
) => {
    return (
        bonusProducts &&
        bonusProducts.map((bonusProduct, idx) => {
            const reducedDimensions = getDimensionsText(bonusProduct);
            const originalPriceText = context.cultureFormatter.formatCurrency(bonusProduct.AdjustedPrice);

            return (
                <div key={idx} className={`${className}__dialog__bonus-product-line`}>
                    <div className='bonus-item'>
                        <span className='icon-add'></span>
                        <span className={`${className}__dialog__product-title`}>
                            {bonusProduct.Name}
                            {reducedDimensions ? `, ${reducedDimensions}` : ''}
                        </span>
                    </div>
                    <div className={`${className}__dialog__product-price`}>
                        <span className={`${className}__dialog__bonus-label`}>Bonus!</span>
                        <span className={`${className}__dialog__bonus-original-price msc-price__strikethrough`}>{originalPriceText}</span>
                        <span className={`${className}__dialog__bonus-free-price`}>$0.00</span>
                        {bonusProduct.shipped && (
                            <span className={`${className}__dialog__bonus-shipped`}>{shippedLabel ?? 'will be shipped'}</span>
                        )}
                    </div>
                </div>
            );
        })
    );
};

export const Popup = (popupProps: IPopupProps) => {
    const {
        dialogStrings,
        className,
        imageSettings,
        gridSettings,
        data: { product, price, bonusProducts },
        productQuantity,
        priceComponent,
        navigationUrl,
        modalOpen,
        telemetryContent,
        setModalOpen,
        context
    } = popupProps;
    const closeModal = React.useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);
    if (!dialogStrings || !product) {
        return null;
    }
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.ContinueShopping);
    const continueShoppingAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.ViewBagAndCheckout;
    const viewBagAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const quantity = (productQuantity !== undefined ? productQuantity : 1) + (bonusProducts?.length ?? 0);
    const itemsString =
        quantity === 1 ? dialogStrings.headerItemOneText : dialogStrings.headerItemFormatText.replace('{0}', quantity.toString());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    const shouldDisplayUnitOfMeasure =
        context.app.config?.unitOfMeasureDisplayType && context.app.config.unitOfMeasureDisplayType !== 'none';

    const reducedDimensions: string = getDimensionsText(product);

    return (
        <Modal isOpen={modalOpen} onClosed={closeModal} className={`${className}__dialog`} toggle={closeModal}>
            <ModalHeader toggle={closeModal} className={`${className}__dialog__header`}>
                <span>{itemsString}</span>
                <span>{dialogStrings.headerMessageText}</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <Image
                    requestContext={context.actionContext.requestContext}
                    className={`${className}__dialog__image`}
                    src={product.PrimaryImageUrl || ''}
                    fallBackSrc={fallbackImageUrl}
                    altText={product.Name}
                    gridSettings={gridSettings || {}}
                    imageSettings={imageSettings}
                    loadFailureBehavior='empty'
                />
                <span className={`${className}__dialog__product-title`}>{product.Name}</span>
                {reducedDimensions !== '' ? <div className={`${className}__dialog__product-variants`}>{reducedDimensions}</div> : ''}
                {price ? <div className={`${className}__dialog__product-price`}>{priceComponent}</div> : ''}
                {product.DefaultUnitOfMeasure && shouldDisplayUnitOfMeasure ? (
                    <div className={`${className}__dialog__product-unitOfMeasure`}>
                        <span>{product.DefaultUnitOfMeasure}</span>
                    </div>
                ) : (
                    ''
                )}
                {bonusProducts && renderBonusItemLines(bonusProducts, context, className)}
            </ModalBody>
            <ModalFooter className={`${className}__dialog__footer`}>
                <Button
                    className={`${className}__dialog__go-to-cart`}
                    href={navigationUrl}
                    aria-label={dialogStrings.goToCartText}
                    {...viewBagAttribute}
                >
                    {dialogStrings.goToCartText}
                </Button>
                <Button
                    className={`${className}__dialog__back-to-shopping`}
                    onClick={closeModal}
                    aria-label={dialogStrings.continueShoppingText}
                    {...continueShoppingAttribute}
                >
                    {dialogStrings.continueShoppingText}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default Popup;
